import { useRef, useState } from 'react'
import { apiGet, apiPost } from '../../generic/Api_Functions'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { Button, IconButton } from '@mui/material'
import WorkHistoryIcon from '@mui/icons-material/WorkHistory'
import { Tune, Visibility, VisibilityOff } from '@mui/icons-material'
import SmartMateriaUITable from '../../generic/SmartMateriaUITable'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import createDialogTitle from '../../generic/utilities/dialogUtil'
import PartPresetHistory from './PartPresetHistory'
import PartPresetForm from './PartPresetForm'
import { addPositiveSymbol } from './symbol_utils'
import { useAuthorization } from '../../generic/hooks/useAuthorization'

const PartPreset = ({ part }) => {
  const { t_ } = useSmartTranslation()
  const [forceValuesChange, setForceValuesChange] = useState(null)
  const [selectedRow, setSelectedRow] = useState([])
  const [showPartPresetHistory, setShowPartPresetHistory] = useState(false)
  const [openForm, setOpenForm] = useState(false)
  const [forceLoad, setForceLoad] = useState(false)
  const [hideOffsets, setHideOffsets] = useState(false)
  const formRef = useRef(null)
  const isUserAuthorized = useAuthorization()

  const variables = ['x', 'y', 'z', 'a', 'b', 'c']

  const presetDataFetch = async (pagination) => {
    const data = await apiGet('fixturePartPreset/' + part.id, pagination)
    data.content.forEach((row) => {
      variables.forEach((variable) => {
        row[variable + '_Offset'] = addPositiveSymbol(row[variable + '_Offset'])
      })
    })
    return data
  }

  const validateOffset = (row, variable) => {
    const offset = row[variable + '_Offset']
    const nominal = row[variable + '_Nominal']
    if (offset === '' || offset === null) {
      if (nominal === '' || nominal === null) {
        return undefined
      } else {
        return t_('Offset should not be empty')
      }
    } else {
      if ((nominal === '' || nominal === null) && offset !== 0) {
        return t_('Nominal should not be empty')
      }
    }

    const offsetNum = parseFloat(offset)
    if (isNaN(offsetNum)) {
      return undefined
    }
  }

  const handleFixtureChange = async (row) => {
    const data = await apiGet('fixtureReferencePartReferencePreset/' + part.partReference.id + '/' + row.fixture.id)
    const newRow = { valuesToChange: [] }
    newRow['smart-table-index'] = row['smart-table-index']

    variables.forEach(field => {
      const nominalField = field + '_Nominal'
      const value = data[field] !== undefined && data[field] !== null ? data[field] : ''
      newRow.valuesToChange.push({ field: nominalField, value })
    })

    setForceValuesChange({
      rowsToChange: [
        newRow
      ]
    })
  }

  const variableColumns = variables.flatMap((variable) => {
    return [
      {
        name: variable.toUpperCase() + t_(' Nominal'),
        field: variable + '_Nominal',
        readOnly: (row) => {
          if (row['smart-table-index'] === -1) {
            return true
          }
        },
        condition: (row) => undefined
      },
      {
        name: variable.toUpperCase() + t_(' Offset'),
        field: variable + '_Offset',
        hide: hideOffsets,
        readOnly: (row) => {
          const offset = row[variable + '_Offset']
          const nominal = row[variable + '_Nominal']
          return ((nominal === '' || nominal === null) && (offset === 0))
        },
        condition: (row) => validateOffset(row, variable),
        defaultNewValue: 0
      }
    ]
  })

  const presetColumns = [
    {
      name: t_('Fixture'),
      field: 'fixture.code',
      type: 'smartSelect',
      condition: (row) => undefined,
      edit: {
        source: async () => {
          return await apiGet('fixtureInstancesByPartReference/' + part.partReference.id)
        },
        id: 'id',
        field: 'code',
        onClick_render: (row) => { handleFixtureChange(row) }
      },
      mandatory: true
    },
    {
      name: t_('Preset number'),
      field: 'presetNumber',
      numeric: true
    },
    ...variableColumns,
    {
      name: t_('Registered date'),
      field: 'registeredDate',
      type: 'dateTimePicker',
      readOnly: true,
      render: (row) => {
        if (row['smart-table-index'] === -1) {
          return null
        } else {
          const formattedDateTime = new Date(row.registeredDate).toLocaleString('es-ES', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          })
          return (
            <div>
              {formattedDateTime}
            </div>
          )
        }
      }
    }
  ]

  const actionsPreset = {
    create: {
      fetchFunction: (row) => {
        row.part = { id: part.id, code: part.code, name: part.name }
        return apiPost('fixturePartPreset', row)
      },
      disabled: !isUserAuthorized('FEATURE_PART_INSTANCE_PRESET_ADD')
    },
    custom: [
      {
        name: t_('View history'),
        render: (row) => {
          return (
            <IconButton>
              <WorkHistoryIcon />
            </IconButton>
          )
        },
        onClick: (row) => {
          setSelectedRow(row)
          setShowPartPresetHistory(true)
        },
        disabled: !isUserAuthorized('FEATURE_PART_INSTANCE_PRESET_VIEW_HISTORY')
      }
    ]
  }

  return (
    <>
      <SmartDialog
        renderComponent={<PartPresetHistory part={part} preset={selectedRow} />}
        setOpen={showPartPresetHistory}
        cancel={false}
        accept={false}
        close
        closeCallback={() => { setShowPartPresetHistory(false) }}
      />
      <SmartDialog
        closeCallback={() => setOpenForm(false)}
        accept
        acceptLabel='Save'
        acceptCallback={() => formRef.current?.submit()}
        cancel={false}
        close
        title={createDialogTitle({ color: 'primary', title: t_('Add preset'), icon: Tune })}
        renderComponent={
          <PartPresetForm
            ref={formRef}
            part={part}
            onClose={() => setOpenForm(false)}
            onSaved={() => setForceLoad((previous) => !previous)}
          />
        }
        setOpen={openForm}
      />
      <SmartMateriaUITable
        columns={presetColumns}
        dataFetch={presetDataFetch}
        actions={actionsPreset}
        forceValuesChange={forceValuesChange}
        onEditIndexChanged={() => setForceValuesChange(null)}
        pageable
        forceLoad={forceLoad}
        onAddElementClick={() => setOpenForm(true)}
        customAddRecordClick
        toolbar={{
          renderSticky: {
            fetchFunction: () => (
              <Button
                variant='text'
                color='primary'
                startIcon={hideOffsets ? <Visibility /> : <VisibilityOff />}
                onClick={() => setHideOffsets((previous) => !previous)}
              >
                {hideOffsets ? t_('Show offsets') : t_('Hide offsets')}
              </Button>
            )
          }
        }}
      />
    </>
  )
}

export default PartPreset
